import { createDiceRollSlideShow } from "@/game/utils/diceDisplayUtils.ts";
import { GameLogger } from "@/game/utils/gameLogger.ts";
import { playSound } from "@/lib/audio";
import { getGameMode } from "@nextgammon/shared";
import { useEffect, useMemo, useRef } from "react";

import { Easing, Group, Tween } from "@tweenjs/tween.js";
import * as PIXI from "pixi.js";
import { Assets, Texture } from "pixi.js";

interface UseDiceRollInterstitialProps {
  diceValues: number[];
  diceTextures: Texture[];
  shouldShowRoll: boolean;
  diceArea: [number, number, number, number];
  playArea: [number, number, number, number];
  diceScale: number;
  boardScale: number;
  isPlayerTurn: boolean;
  playerUsername?: string;
  opponentUsername?: string;
  overlayInterface: any;
  onRollAnimationComplete: () => void;
  gameMode: string; // NEW: Game mode for animation timing
}

export function useDiceRollInterstitial({
  diceValues,
  diceTextures,
  shouldShowRoll,
  diceArea,
  playArea,
  diceScale,
  boardScale,
  isPlayerTurn,
  playerUsername,
  opponentUsername,
  overlayInterface,
  onRollAnimationComplete,
  gameMode
}: UseDiceRollInterstitialProps) {
  const inProgressRef = useRef<boolean>(false);
  const completedRef = useRef<boolean>(false);
  // Load the background for dice rolls
  const rollBG = useMemo(() => Assets.get("dice-roll-bg"), []);

  // Calculate animation durations based on game mode
  const mode = getGameMode(gameMode);
  const ROLL_ANIMATION_TIME_NORMAL = Math.round(3000 * mode.animation.diceRollDurationMultiplier);
  const ROLL_ANIMATION_TIME_DOUBLES = Math.round(3500 * mode.animation.diceRollDurationMultiplier);

  const keyBG = `dice-roll-${isPlayerTurn ? "player" : "opponent"}-bg`;
  const keyLabel1 = `dice-roll-${isPlayerTurn ? "player" : "opponent"}-label-1`;
  const keyLabel2 = `dice-roll-${isPlayerTurn ? "player" : "opponent"}-label-2`;
  const keySlides = `dice-roll-${isPlayerTurn ? "player" : "opponent"}-slides-`;

  GameLogger.logDebug(
    "useDiceRollInterstitial",
    `Rendering dice: interstitial: ${shouldShowRoll}, isPlayerTurn: ${isPlayerTurn}, diceValues: ${JSON.stringify(diceValues)}`
  );

  useEffect(() => {
    if (!isPlayerTurn) {
      GameLogger.logInfo("useDiceRollInterstitial", "Clearing animation complete state.");
      completedRef.current = false;
    }
  }, [isPlayerTurn]);

  const cleanupRollAnimation = () => {
    if (overlayInterface) {
      overlayInterface.removeSprite(keyBG);
      overlayInterface.removeSprite(keyLabel1);
      overlayInterface.removeSprite(keyLabel2);
      for (let i = 1; i <= 4; i++) {
        overlayInterface.removeSprite(keySlides + i, 100);
      }
    }
  };

  // --- Roll Animation Helpers ---

  const setupRollAnimationBackground = (
    playAreaCenter: { x: number; y: number },
    totalRollAnimTime: number
  ) => {
    const spriteBG = new PIXI.Sprite(rollBG);
    spriteBG.anchor.set(0.5, 0.5);
    spriteBG.position.set(playAreaCenter.x, playAreaCenter.y);
    spriteBG.scale.set(boardScale, boardScale);
    spriteBG.roundPixels = true;

    const groupBG = new Group();
    const alphaTweenInBG = new Tween({ alpha: 0 })
      .to({ alpha: 1 }, 500)
      .onUpdate((values: { alpha: number }) => {
        spriteBG.alpha = values.alpha;
      });
    groupBG.add(alphaTweenInBG);

    const alphaTweenOutBG = new Tween({ alpha: 1 })
      .to({ alpha: 0 }, 500)
      .delay(Math.max(0, totalRollAnimTime - 500))
      .onUpdate((values: { alpha: number }) => {
        spriteBG.alpha = values.alpha;
      });
    groupBG.add(alphaTweenOutBG);

    overlayInterface?.addSpriteWithTween(keyBG, spriteBG, groupBG, true);
  };

  const setupRollAnimationSlideshow = (
    playAreaCenter: { x: number; y: number },
    diceWidth: number,
    diceHeight: number,
    doubles: boolean,
    totalRollAnimTime: number
  ): number => {
    let rollDuration = 0;
    const doublesX = playAreaCenter.x;
    const doublesY = playAreaCenter.y;
    const endX = diceArea[0] + diceArea[2] / 2;
    const endY = diceArea[1] + diceHeight / 2;
    const doublesOffset = diceHeight * 0.75;

    if (diceValues!.length > 0) {
      const { sprite, group, duration } = createDiceRollSlideShow(
        diceTextures,
        diceValues![0] - 1,
        totalRollAnimTime,
        0,
        boardScale,
        { x: playArea[0], y: playArea[1] },
        { x: playAreaCenter.x - diceWidth * 0.75, y: playAreaCenter.y },
        diceScale,
        { x: endX, y: endY },
        doubles ? { x: doublesX - diceWidth * 0.75, y: doublesY - doublesOffset } : undefined
      );
      rollDuration = duration;
      overlayInterface?.addSpriteWithTween(keySlides + 1, sprite, group, true);
    }
    if (diceValues!.length > 1) {
      const { sprite, group } = createDiceRollSlideShow(
        diceTextures,
        diceValues![1] - 1,
        totalRollAnimTime,
        0,
        boardScale,
        { x: playArea[0], y: playArea[1] },
        { x: playAreaCenter.x + diceWidth * 0.75, y: playAreaCenter.y },
        diceScale,
        { x: endX, y: endY + diceArea[3] / 4 },
        doubles ? { x: doublesX + diceWidth * 0.75, y: doublesY - doublesOffset } : undefined
      );
      overlayInterface?.addSpriteWithTween(keySlides + 2, sprite, group, true);
    }
    if (doubles && diceValues!.length > 2) {
      const { sprite, group } = createDiceRollSlideShow(
        diceTextures,
        diceValues![2] - 1,
        totalRollAnimTime,
        rollDuration,
        boardScale,
        { x: playArea[0], y: playArea[1] },
        { x: playAreaCenter.x - diceWidth * 0.75, y: playAreaCenter.y },
        diceScale,
        { x: endX, y: endY + (diceArea[3] / 4) * 2 },
        doubles ? { x: doublesX - diceWidth * 0.75, y: doublesY + doublesOffset } : undefined
      );
      overlayInterface?.addSpriteWithTween(keySlides + 3, sprite, group, true);
    }
    if (doubles && diceValues!.length > 3) {
      const { sprite, group } = createDiceRollSlideShow(
        diceTextures,
        diceValues![3] - 1,
        totalRollAnimTime,
        rollDuration,
        boardScale,
        { x: playArea[0], y: playArea[1] },
        { x: playAreaCenter.x + diceWidth * 0.75, y: playAreaCenter.y },
        diceScale,
        { x: endX, y: endY + (diceArea[3] / 4) * 3 },
        doubles ? { x: doublesX + diceWidth * 0.75, y: doublesY + doublesOffset } : undefined
      );
      overlayInterface?.addSpriteWithTween(keySlides + 4, sprite, group, true);
    }
    return rollDuration;
  };

  const setupRollAnimationLabels = (
    playAreaCenter: { x: number; y: number },
    diceHeight: number,
    doubles: boolean,
    rollDuration: number,
    totalRollAnimTime: number
  ) => {
    const labelStyle = new PIXI.TextStyle({
      fontFamily: "Arial",
      fontSize: 96 * boardScale,
      fill: "#ffd960",
      fontWeight: "bold",
      stroke: 0x000000,
      strokeThickness: 2,
      align: "center"
    });
    const yourTurnLabel = new PIXI.Text(
      isPlayerTurn ? "Your Turn" : `${opponentUsername || "Opponent"}'s Turn`,
      labelStyle
    );
    yourTurnLabel.anchor.set(0.5, 1);
    yourTurnLabel.position.set(playAreaCenter.x, playAreaCenter.y - diceHeight * 0.75);
    yourTurnLabel.alpha = 0;

    const groupLabel1 = new Group();
    const alphaTweenInLabel1 = new Tween({ alpha: 0 })
      .to({ alpha: 1 }, 500)
      .onUpdate((values: { alpha: number }) => {
        yourTurnLabel.alpha = values.alpha;
      });
    groupLabel1.add(alphaTweenInLabel1);

    const alphaTweenOutLabel1 = new Tween({ alpha: 1 })
      .to({ alpha: 0 }, 500)
      .delay(Math.max(0, totalRollAnimTime - 500))
      .onUpdate((values: { alpha: number }) => {
        yourTurnLabel.alpha = values.alpha;
      });
    groupLabel1.add(alphaTweenOutLabel1);

    if (doubles) {
      const doublesMoveTween = new Tween(yourTurnLabel)
        .to(
          {
            y: playAreaCenter.y - diceHeight * 1.5 - playArea[1]
          },
          500
        )
        .easing(Easing.Quadratic.InOut)
        .delay(rollDuration);
      groupLabel1.add(doublesMoveTween);
    }

    overlayInterface?.addSpriteWithTween(keyLabel1, yourTurnLabel, groupLabel1, true);

    if (doubles) {
      const doublesLabel = new PIXI.Text("Doubles!", labelStyle);
      doublesLabel.anchor.set(0.5, 0);
      doublesLabel.position.set(playAreaCenter.x, playAreaCenter.y + diceHeight * 1.5);
      doublesLabel.alpha = 0;

      const groupLabel2 = new Group();
      const alphaTweenInLabel2 = new Tween({ alpha: 0 })
        .to({ alpha: 1 }, 200)
        .delay(rollDuration)
        .onUpdate((values: { alpha: number }) => {
          doublesLabel.alpha = values.alpha;
        });
      groupLabel2.add(alphaTweenInLabel2);

      const alphaTweenOutLabel2 = new Tween({ alpha: 1 })
        .to({ alpha: 0 }, 500)
        .delay(Math.max(0, totalRollAnimTime - 500))
        .onUpdate((values: { alpha: number }) => {
          doublesLabel.alpha = values.alpha;
        });
      groupLabel2.add(alphaTweenOutLabel2);

      overlayInterface?.addSpriteWithTween(keyLabel2, doublesLabel, groupLabel2, true);
    }
  };

  const setAnimationCompleted = (triggerCallback: boolean) => {
    cleanupRollAnimation();
    inProgressRef.current = false;
    completedRef.current = true;

    GameLogger.logInfo(
      "useDiceRollInitial",
      "Set roll animation completed triggerCallback: " + triggerCallback
    );

    if (triggerCallback) {
      onRollAnimationComplete();
    }
  };

  // Handle the roll interstitial animation
  useEffect(() => {
    GameLogger.logDebug(
      "useDiceRollInterstitial",
      `useEffect: shouldShowRoll: ${shouldShowRoll}, diceValues: ${JSON.stringify(diceValues)}`
    );

    if (!shouldShowRoll || !diceValues || !overlayInterface || !diceTextures || !rollBG) {
      return;
    }

    const playAreaCenter = { x: playArea[0] + playArea[2] / 2, y: playArea[1] + playArea[3] / 2 };

    if (inProgressRef.current) {
      GameLogger.logInfo(
        "useDiceRollInterstitial",
        `Roll animation in progress, shouldShowRoll: ${shouldShowRoll}`
      );

      if (!shouldShowRoll) {
        setAnimationCompleted(false);
      }
      return;
    }

    if (shouldShowRoll) {
      const doubles = diceValues.length > 2;
      const totalRollAnimTime = doubles ? ROLL_ANIMATION_TIME_DOUBLES : ROLL_ANIMATION_TIME_NORMAL;

      GameLogger.logInfo("useDiceRollInterstitial", "Starting roll animation");
      inProgressRef.current = true;
      completedRef.current = false;

      // Play dice roll sound
      playSound("dice-roll");

      const diceWidth = diceTextures[0].width * diceScale;
      const diceHeight = diceTextures[0].height * diceScale;

      setupRollAnimationBackground(playAreaCenter, totalRollAnimTime);
      const rollDuration = setupRollAnimationSlideshow(
        playAreaCenter,
        diceWidth,
        diceHeight,
        doubles,
        totalRollAnimTime
      );
      setupRollAnimationLabels(
        playAreaCenter,
        diceHeight,
        doubles,
        rollDuration,
        totalRollAnimTime
      );

      const timerId = setTimeout(() => {
        GameLogger.logInfo("useDiceRollInterstitial", "Roll animation complete");

        setAnimationCompleted(true);
      }, totalRollAnimTime);

      return () => {
        GameLogger.logInfo("useDiceRollInterstitial", "Cleaning up roll animation on unmount");
        clearTimeout(timerId);
        setAnimationCompleted(false);
      };
    }
  }, [shouldShowRoll, diceValues]);

  return {
    isInterstitialRollInProgress: inProgressRef.current
  };
}
